import { useCallback } from 'react'

import type { Map } from 'immutable'
import { useDispatch, useSelector } from 'react-redux'

import type { RecipeCoreId } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'
import { trackUserAddRemoveRecipe } from 'actions/loggingmanager'
import * as trackingKeys from 'actions/trackingKeys'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useCurrentCollectionId } from 'hooks/menu-collections'
import { getBasketMenuUuid, getBasketRecipes } from 'selectors/basket'
import { getRecipeIdByCoreIdMap } from 'selectors/menu-recipes/getRecipeIdByCoreIdMap'

import { useRecipeLimitReached } from '../limits'
import { useMenuId } from '../useMenuId'

export type RemoveRecipeFn = ReturnType<typeof useRemoveRecipe>
type RemoveRecipeOptions = {
  markUnsaved?: boolean
}

export const useRemoveRecipe = () => {
  const { numPortions } = useNumPortions()
  const recipesInBasket = useSelector(getBasketRecipes)
  const reachedLimit = useRecipeLimitReached(
    recipesInBasket as unknown as Map<RecipeCoreId, number>,
  )
  const currentMenuId = useMenuId()
  const currentMenuUUID = useSelector(getBasketMenuUuid)
  const currentCollectionId = useCurrentCollectionId()
  const recipeIdByCoreIdMap = useSelector(getRecipeIdByCoreIdMap)
  const dispatch = useDispatch()

  return useCallback(
    (recipeId: RecipeCoreId, view?: string, position?: string, options?: RemoveRecipeOptions) => {
      const { markUnsaved = true } = options ?? {}
      const basketRecipesWithoutRecipeId = Object.keys(recipesInBasket.toJS()).filter(
        (key) => key !== recipeId,
      )
      dispatch({
        type: actionTypes.BASKET_RECIPE_REMOVE,
        recipeId,
        markUnsaved,
        trackingData: {
          actionType: trackingKeys.removeRecipe,
          basketRecipes: basketRecipesWithoutRecipeId,
          boxSize: numPortions,
          boxType: 'chosen',
          menu_id: currentMenuId,
          menu_uuid: currentMenuUUID,
          recipe_id: recipeId,
          recipe_uuid: recipeIdByCoreIdMap[recipeId],
          view,
          position,
          collection: currentCollectionId,
          basket_recipe_count: recipesInBasket.size - 1, // The action is performed in the same time so the size is not updated yet
        },
      })

      if (!reachedLimit) {
        dispatch({
          type: actionTypes.BASKET_LIMIT_REACHED,
          limitReached: reachedLimit,
          trackingData: {
            actionType: trackingKeys.basketLimit,
            limitReached: reachedLimit,
            view,
            source: actionTypes.RECIPE_REMOVED,
          },
        })
      }

      dispatch(trackUserAddRemoveRecipe())
    },
    [
      recipesInBasket,
      dispatch,
      numPortions,
      currentMenuId,
      currentMenuUUID,
      recipeIdByCoreIdMap,
      currentCollectionId,
      reachedLimit,
    ],
  )
}
